import {CLOSING_EVENT} from './index';

export interface ModalConfig<T> {
  /** tag name of modal like custom element  e.g mp-confirm */
  tagName: string;
  /** attributes to pass to the custom element modal when opened */
  attrs: any;
  /** properties to pass to the custom element modal when opened */
  props?: any;
  /** name of events that will close the modal and resolve the promise */
  closeEvents: string[];
  /** function handler to receive the custom event, the promise resolves with this return unless null */
  closeHandler?: (event: CustomEvent) => T | null;
}

// These events are known to be "cancellation" events that will result in the closure of the modal regardless
//  of the `open` attribute we can control. This occurs when a user clicks outside a modal or presses escape.
const knownClosureEvents = [CLOSING_EVENT];

/**
 * This function will wrap any modal with a promise to allow for the invocation through js/ts instead of your template with open/close attributes
 * @param   modalConfig Config object to contain attributes, close events list
 * @returns Promise resolved with the custom event that triggered the closure
 */
export function promisifyModal<T = CustomEvent>({
  tagName,
  attrs = {},
  props = {},
  closeEvents,
  closeHandler,
}: ModalConfig<T>): Promise<T> {
  return new Promise((resolve) => {
    const modal = document.createElement(tagName);
    const eventsToListen = [...closeEvents, ...knownClosureEvents];

    const closeCallback = (ev) => {
      const resolveValue = closeHandler ? closeHandler(ev) : ev;
      if (resolveValue !== null || knownClosureEvents.includes(ev.type)) {
        resolve(resolveValue);
        modal.setAttribute(`open`, `false`);

        eventsToListen.forEach((eventName: string) => modal.removeEventListener(eventName, closeCallback));

        setTimeout(() => document.body.removeChild(modal), 500);
      }
    };

    Object.entries(attrs).forEach(([attr, val]) => modal.setAttribute(attr, String(val)));
    Object.entries(props).forEach(([prop, val]) => (modal[prop] = val));

    eventsToListen.forEach((eventName: string) => modal.addEventListener(eventName, closeCallback));

    document.body.appendChild(modal);
    modal.setAttribute(`open`, `true`);
  });
}
